import { useState, useCallback, useEffect } from 'react';
import { blockApi, pageApi } from '../services/api';
import { createBlock, BLOCK_TYPES } from '../utils/blockTypes';

export const useBlocks = (pageSlug, historyId) => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [pageId, setPageId] = useState(null);

  const getPageId = useCallback(async (slug) => {
    if (!slug) return null;

    try {
      const response = await pageApi.getBySlug(slug, historyId);
      if (response.data?.page?.id) {
        return response.data.page.id;
      }

      const idResponse = await pageApi.getPageIdFromSlug(slug);
      if (idResponse.data?.id) {
        return idResponse.data.id;
      }

      return null;
    } catch (err) {
      console.error('Failed to get page ID from slug:', err);
      return null;
    }
  }, [historyId]);

  const loadBlocks = useCallback(async () => {
    if (!pageSlug) return;

    setLoading(true);
    setError(null);

    try {
      const id = await getPageId(pageSlug);
      if (!id) {
        throw new Error('Page not found');
      }

      setPageId(id);

      const response = await pageApi.getBySlug(pageSlug, historyId);
      setBlocks(Array.isArray(response.data.blocks) ? response.data.blocks : []);
    } catch (err) {
      setError(err.message || 'Failed to load blocks');
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [pageSlug, historyId, getPageId]);

  const saveAllBlocks = useCallback(async () => {
    if (!pageId || !blocks.length) {
      console.log('No blocks to save');
      return false;
    }

    try {
      setLoading(true);
      // IMPORTANT: Preserve all spaces in block content
      // Do NOT trim, strip, or modify any whitespace in titles or content
      const blocksToSave = blocks.map(block => ({
        ...block,
        properties: block.properties ? {
          ...block.properties,
          // Keep title content exactly as entered, with all spaces preserved
          ...(block.properties.title && {
            title: block.properties.title
          }),
          // Keep caption exactly as entered, with all spaces preserved
          ...(block.properties.caption !== undefined && {
            caption: block.properties.caption
          })
        } : block.properties
      }));

      const response = await blockApi.updatePageBlocks(pageId, blocksToSave);

      console.log('✅ Blocks saved successfully - all spaces preserved');
      return true;
    } catch (err) {
      const errorMsg = err.message || 'Failed to save blocks';
      setError(errorMsg);
      console.error('❌ Failed to save blocks:', err);
      alert(`Save failed: ${errorMsg}`);
      return false;
    } finally {
      setLoading(false);
    }
  }, [pageId, blocks]);

  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     if ((e.ctrlKey || e.metaKey) && e.key === 's') {
  //       e.preventDefault();
  //       saveAllBlocks();
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [saveAllBlocks]);

  const addBlock = useCallback(async (type, content = '', properties = {}, position = 'end') => {
    if (!pageId) {
      console.error('No page ID available');
      return null;
    }

    const newBlock = createBlock(type, content, properties);

    try {
      const savedBlock = await blockApi.createBlock(pageId, {
        ...newBlock,
        page_id: pageId,
      });

      setBlocks(prev => {
        if (position === 'end') {
          return [...prev, { ...savedBlock.data, order_index: prev.length }];
        }

        if (selectedBlockId) {
          const selectedIndex = prev.findIndex(b => b.id === selectedBlockId);
          if (selectedIndex >= 0) {
            const updated = [...prev];
            updated.splice(selectedIndex + 1, 0, {
              ...savedBlock.data,
              order_index: selectedIndex + 1
            });

            return updated.map((block, idx) => ({
              ...block,
              order_index: idx
            }));
          }
        }

        return [{ ...savedBlock.data, order_index: 0 }, ...prev];
      });

      setSelectedBlockId(savedBlock.data.id);
      return savedBlock.data.id;
    } catch (error) {
      console.error('Failed to create block:', error);
      return null;
    }
  }, [selectedBlockId, pageId]);

  const updateBlock = useCallback((blockId, updates) => {
    // CRITICAL: Preserve all spaces and whitespace in block content
    // Do NOT trim, strip, or modify content in any way
    setBlocks(prev =>
      prev.map(block =>
        block.id === blockId
          ? {
              ...block,
              ...updates,
              // Ensure properties including titles and captions preserve all spaces
              properties: updates.properties ? {
                ...block.properties,
                ...updates.properties,
                // Keep title content exactly as provided without modification
                title: updates.properties.title || block.properties?.title,
                // Keep caption exactly as provided without modification
                caption: updates.properties.caption !== undefined 
                  ? updates.properties.caption 
                  : block.properties?.caption
              } : block.properties,
              updated_at: new Date().toISOString()
            }
          : block
      )
    );
  }, []);

  const deleteBlock = useCallback((blockId) => {
    const blockToDelete = blockApi.deleteBlock(blockId);

    setBlocks(prev => {
      const filtered = prev.filter(block => block.id !== blockId);
      return filtered.map((block, idx) => ({
        ...block,
        order_index: idx
      }));
    });

    if (selectedBlockId === blockId) {
      setSelectedBlockId(null);
    }
  }, [selectedBlockId]);

  const moveBlock = useCallback(async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;

    setBlocks(prev => {
      const newBlocks = [...prev];
      const [movedBlock] = newBlocks.splice(fromIndex, 1);
      newBlocks.splice(toIndex, 0, movedBlock);

      return newBlocks.map((block, idx) => ({
        ...block,
        order_index: idx
      }));
    });
  }, []);

  useEffect(() => {
    if (pageSlug) {
      loadBlocks();
    }
  }, [pageSlug, loadBlocks]);
  return {
    blocks,
    loading,
    error,
    selectedBlockId,
    setSelectedBlockId,
    pageId,
    addBlock,
    updateBlock,
    deleteBlock,
    moveBlock,
    addImageBlock: useCallback((imageUrl, caption = '', position = 'below') => {
      return addBlock(BLOCK_TYPES.IMAGE, imageUrl, { caption }, position);
    }, [addBlock]),
    addFileBlock: useCallback((fileUrl, fileName, fileSize, position = 'below') => {
      return addBlock(BLOCK_TYPES.FILE, fileUrl, {
        name: fileName,
        size: fileSize
      }, position);
    }, [addBlock]),
    addLinkBlock: useCallback((url, text = '', position = 'below') => {
      return addBlock(BLOCK_TYPES.LINK, url, {
        text: text || url
      }, position);
    }, [addBlock]),
    saveAllBlocks,
    loadBlocks,
    setBlocks,
  };
};